import { ChatToolCall, ChatTurnArtifacts } from './orchestrator-chat.models';

export interface CatalogList<T> {
  count: number;
  items: T[];
}

export interface ChatSessionSummary {
  session_id: string;
  title?: string | null;
  locale?: string | null;
  starred: boolean;
  archived: boolean;
  message_count: number;
  created_at_utc: string;
  updated_at_utc: string;
  last_message_at_utc?: string | null;
}

export interface ChatMessageRecord {
  id: number;
  role: 'user' | 'assistant' | string;
  text: string;
  correlation_id?: string | null;
  artifacts?: ChatTurnArtifacts | null;
  tool_calls?: ChatToolCall[] | null;
  error?: string | null;
  created_at_utc: string;
}

export interface ChatSessionDetails {
  session: ChatSessionSummary;
  messages: ChatMessageRecord[];
}

export interface ChatSessionPatch {
  title?: string;
  starred?: boolean;
  archived?: boolean;
}

export interface ChatSessionsQuery {
  search?: string;
  starred?: boolean;
  archived?: 'true' | 'false' | 'all';
  take?: number;
}

export type ReportJobStatus = 'queued' | 'running' | 'done' | 'failed' | string;

export interface ReportJobItem {
  job_id: string;
  status: ReportJobStatus;
  progress_pct: number;
  format: string;
  title?: string | null;
  locale?: string | null;
  template_id?: string | null;
  session_id?: string | null;
  subscription_id?: string | null;
  report_id?: string | null;
  download_url?: string | null;
  error?: string | null;
  created_at_utc: string;
  started_at_utc?: string | null;
  finished_at_utc?: string | null;
}

export interface ReportArtifactItem {
  report_id: string;
  job_id?: string | null;
  session_id?: string | null;
  title?: string | null;
  file_name: string;
  format: string;
  content_type: string;
  size_bytes: number;
  locale?: string | null;
  template_id?: string | null;
  as_of?: string | null;
  warnings: string[];
  download_url?: string | null;
  created_at_utc: string;
  expires_at_utc?: string | null;
}

export interface ReportsQuery {
  format?: string;
  from?: string;
  to?: string;
  take?: number;
}

export interface JobsQuery extends ReportsQuery {
  status?: string;
  session_id?: string;
}

export type SubscriptionFormat = 'csv' | 'geojson' | 'xlsx' | 'html' | 'chat_md';
export type SubscriptionChannel = 'file' | 'telegram' | 'email' | 'dashboard';

export const SUBSCRIPTION_FORMATS: SubscriptionFormat[] = ['xlsx', 'csv', 'html', 'chat_md', 'geojson'];
export const SUBSCRIPTION_CHANNELS: SubscriptionChannel[] = ['file', 'email', 'telegram', 'dashboard'];

export interface ReportTemplateItem {
  template_id: string;
  category: string;
  title_ru: string;
  title_uz: string;
  description_ru: string;
  description_uz: string;
  formats: string[];
}

export interface ConstructorTemplateItem {
  template_id: string;
  title: string;
  updated_at_utc?: string;
}

export interface SaveConstructorTemplateRequest {
  template_id?: string | null;
  title: string;
  period_from?: string | null;
  period_to?: string | null;
  geo_id?: string | null;
  crop_id?: string | null;
  sections: Array<{ dataset_id: string; title: string; columns: string[] }>;
}

export interface SubscribeConstructorTemplateRequest {
  format: string;
  days?: number[];
  time?: string | null;
  cron?: string | null;
  trigger_on_new_pass?: boolean;
}

export interface ApplyConstructorTemplateResponse {
  session_id: string;
  template_id: string;
  title: string;
  period_from?: string | null;
  period_to?: string | null;
  geo_id?: string | null;
  crop_id?: string | null;
  sections: Array<{
    dataset_id: string;
    tool: string;
    title: string;
    selected_columns?: string[];
    meta?: Record<string, unknown> | null;
    preview_rows?: Record<string, unknown>[];
    columns?: ReportColumnOption[];
  }>;
}

export interface RefreshDatasetRequest {
  geo_id?: string | null;
  period_from?: string | null;
  period_to?: string | null;
  crop_id?: string | null;
}

export interface RefreshDatasetResponse {
  dataset_id: string;
  tool: string;
  meta?: Record<string, unknown> | null;
  preview_rows?: Record<string, unknown>[];
  columns?: ReportColumnOption[];
}

export interface ReportColumnOption {
  key: string;
  label_ru?: string;
  label_uz?: string;
  label_en?: string;
  default_selected?: boolean;
  identifier?: boolean;
}

export interface ComposeReportRequest {
  title?: string | null;
  period_from?: string | null;
  period_to?: string | null;
  geo_id?: string | null;
  crop_id?: string | null;
  format: string;
  locale: string;
  sections: Array<{ dataset_id: string; title: string; columns: string[] }>;
}

export interface CreateReportRequest {
  template_id: string;
  title?: string | null;
  geo_id: string;
  geo_name?: string | null;
  from: string;
  to: string;
  season_cycle: string;
  format: string;
  locale: string;
}

export interface SubscriptionItem {
  subscription_id: string;
  title?: string | null;
  format: string;
  delivery_channel: string;
  cron?: string | null;
  trigger_on_new_pass: boolean;
  geo_id?: string | null;
  locale?: string | null;
  active: boolean;
  created_at_utc: string;
  updated_at_utc: string;
  last_run_at_utc?: string | null;
  next_run_at_utc?: string | null;
  last_pass_date?: string | null;
  template_id?: string | null;
  params?: string | null;
}

export interface CreateSubscriptionRequest {
  title?: string | null;
  dataset?: unknown;
  template_id?: string | null;
  format: string;
  delivery_channel?: string | null;
  cron?: string | null;
  days?: number[];
  time?: string | null;
  trigger_on_new_pass?: boolean;
  geo_id?: string | null;
  geo_name?: string | null;
  season_cycle?: string | null;
  window?: string | null;
  locale?: string | null;
}

export interface UpdateSubscriptionRequest {
  title?: string;
  cron?: string;
  clear_cron?: boolean;
  delivery_channel?: string;
  trigger_on_new_pass?: boolean;
  geo_id?: string;
  locale?: string;
  format?: string;
  template_id?: string;
  geo_name?: string;
  season_cycle?: string;
  window?: string;
  days?: number[];
  time?: string;
  active?: boolean;
}

export interface SubscriptionRunItem {
  id: number;
  subscription_id: string;
  job_id?: string | null;
  status: ReportJobStatus;
  trigger_reason?: string | null;
  report_id?: string | null;
  download_url?: string | null;
  started_at_utc: string;
  finished_at_utc?: string | null;
  error?: string | null;
}

export function isActiveJobStatus(status: string | null | undefined): boolean {
  return status === 'queued' || status === 'running';
}
